/**
 * Made-up calls with clients for the Calls preview (CALL-1…4, planned for V2).
 * Leads and staff come from mocks/shared.ts; every person, email and word here is
 * invented, and the pages that show it carry a PreviewBanner.
 */

/** How a call found its lead, automatically (CALL-2). */
export type CallLinkMethod = 'attendee_email' | 'calendar_invite' | 'other_email' | 'telegram_booking';

/** The note taker is V2 and its tool is still open (CALL-4, Q14). */
export type NoteTakerStatus = 'ready' | 'processing' | 'none';

export type MockCall = {
  id: string;
  title: string;
  startedAt: string;
  durationMin: number;
  leadEmail: string;
  staffId: string;
  link: { method: CallLinkMethod; detail: string };
  participants: { name: string; email: string; side: 'client' | 'team' }[];
  noteTaker: NoteTakerStatus;
  summary: string[];
  actionItems: { id: string; text: string }[];
  transcript: { at: string; speaker: string; text: string }[];
};

export const LINK_METHOD_LABELS: Record<CallLinkMethod, string> = {
  attendee_email: 'Attendee email',
  calendar_invite: 'Calendar invite',
  other_email: 'Matched, other email',
  telegram_booking: 'Booked from Telegram',
};

export const NOTE_TAKER_LABELS: Record<NoteTakerStatus, string> = {
  ready: 'Notes ready',
  processing: 'Processing',
  none: 'No notes',
};

export const MOCK_CALLS: MockCall[] = [
  {
    id: 'call-maria-license',
    title: 'Curaçao license and timelines',
    startedAt: '2026-10-26T14:00:00Z',
    durationMin: 32,
    leadEmail: 'maria.invest@example.com',
    staffId: 'staff-sara',
    link: { method: 'attendee_email', detail: 'maria.invest@example.com was on the invite and joined with it.' },
    participants: [
      { name: 'Maria Gomez', email: 'maria.invest@example.com', side: 'client' },
      { name: 'Sara Ionescu', email: 'sara@prisma.example', side: 'team' },
    ],
    noteTaker: 'ready',
    summary: [
      'Maria wants a Curaçao license and has a budget of $20k–50k.',
      'She asked how long the license takes: about 6 to 8 weeks once the documents are in.',
      'She prefers to launch in Peru first, then Mexico.',
    ],
    actionItems: [
      { id: 'a1', text: 'Send Maria the license document checklist' },
      { id: 'a2', text: 'Share two example casinos running in Peru' },
    ],
    transcript: [
      { at: '00:02', speaker: 'Sara Ionescu', text: 'Thanks for making time, Maria. You wrote about the Curaçao license?' },
      { at: '00:09', speaker: 'Maria Gomez', text: 'Yes. How long does it take from the day we send the documents?' },
      { at: '00:15', speaker: 'Sara Ionescu', text: 'Usually six to eight weeks. I can send you the list of documents today.' },
      { at: '00:24', speaker: 'Maria Gomez', text: 'Perfect, and we would start with Peru.' },
    ],
  },
  {
    id: 'call-jonas-onboarding',
    title: 'Onboarding call',
    startedAt: '2026-10-24T10:30:00Z',
    durationMin: 25,
    leadEmail: 'partners@luckypartners.example',
    staffId: 'staff-mihai',
    link: {
      method: 'other_email',
      detail: 'Jonas joined as jonas.b@mailbox.example. The calendar invite went to partners@luckypartners.example, so the call was matched to that lead.',
    },
    participants: [
      { name: 'Jonas Berg', email: 'jonas.b@mailbox.example', side: 'client' },
      { name: 'Mihai Pop', email: 'mihai@prisma.example', side: 'team' },
    ],
    noteTaker: 'ready',
    summary: [
      'Lucky Partners runs affiliate sites in Brazil and wants its own casino.',
      'Jonas asked about PIX deposits and how fast payouts are.',
      'He will register on the platform this week.',
    ],
    actionItems: [
      { id: 'a1', text: 'Check that Jonas registered and started the wizard' },
      { id: 'a2', text: 'Send the payment methods overview for Brazil' },
    ],
    transcript: [
      { at: '00:01', speaker: 'Mihai Pop', text: 'Hi Jonas, I see you joined from another address. No problem.' },
      { at: '00:07', speaker: 'Jonas Berg', text: 'Yes, that is my personal one. We mostly do affiliate traffic in Brazil.' },
      { at: '00:18', speaker: 'Jonas Berg', text: 'Do players get PIX deposits from day one?' },
      { at: '00:22', speaker: 'Mihai Pop', text: 'They do. PIX is one of the first methods we switch on.' },
    ],
  },
  {
    id: 'call-carla-deploy',
    title: 'Deployment check-in',
    startedAt: '2026-10-23T16:00:00Z',
    durationMin: 18,
    leadEmail: 'carla@example.com',
    staffId: 'staff-sara',
    link: { method: 'calendar_invite', detail: 'Booked from the calendar link sent to carla@example.com.' },
    participants: [
      { name: 'Carla Souza', email: 'carla@example.com', side: 'client' },
      { name: 'Sara Ionescu', email: 'sara@prisma.example', side: 'team' },
    ],
    noteTaker: 'ready',
    summary: ['Aura Bet is paid and deploying.', 'Carla wants OXXO added as a payment method before launch.'],
    actionItems: [{ id: 'a1', text: 'Ask payments whether OXXO can be on at launch' }],
    transcript: [
      { at: '00:03', speaker: 'Carla Souza', text: 'When does Aura Bet go live?' },
      { at: '00:06', speaker: 'Sara Ionescu', text: 'The deployment finishes this week. I will confirm the day.' },
      { at: '00:11', speaker: 'Carla Souza', text: 'And can we have OXXO from the start?' },
    ],
  },
  {
    id: 'call-alex-crypto',
    title: 'Crypto payments',
    startedAt: '2026-10-22T12:00:00Z',
    durationMin: 41,
    leadEmail: 'cryptoalex@example.com',
    staffId: 'staff-mihai',
    link: { method: 'telegram_booking', detail: 'Booked with the link the team posted in the Telegram group with @cryptoalex88.' },
    participants: [
      { name: 'Alex Novak', email: 'alex.novak@mailbox.example', side: 'client' },
      { name: 'Mihai Pop', email: 'mihai@prisma.example', side: 'team' },
    ],
    noteTaker: 'processing',
    summary: [],
    actionItems: [],
    transcript: [],
  },
  {
    id: 'call-laura-config',
    title: 'Casino configuration',
    startedAt: '2026-10-21T09:00:00Z',
    durationMin: 22,
    leadEmail: 'info@gamingventures.example',
    staffId: 'staff-sara',
    link: { method: 'attendee_email', detail: 'info@gamingventures.example was on the invite and joined with it.' },
    participants: [
      { name: 'Laura Chen', email: 'info@gamingventures.example', side: 'client' },
      { name: 'Sara Ionescu', email: 'sara@prisma.example', side: 'team' },
    ],
    noteTaker: 'ready',
    summary: ['Laura is halfway through the wizard.', 'She asked which game providers are included and how to add live tables.'],
    actionItems: [{ id: 'a1', text: 'Send the list of game providers' }],
    transcript: [
      { at: '00:02', speaker: 'Laura Chen', text: 'I am on the games step. Which providers come with it?' },
      { at: '00:08', speaker: 'Sara Ionescu', text: 'More than eighty. I will send you the list after the call.' },
    ],
  },
  {
    id: 'call-royal-growth',
    title: 'Live studio and growth',
    startedAt: '2026-10-15T13:30:00Z',
    durationMin: 15,
    leadEmail: 'owner@royalfortune.example',
    staffId: 'staff-mihai',
    link: { method: 'attendee_email', detail: 'owner@royalfortune.example was on the invite and joined with it.' },
    participants: [
      { name: 'Victor Hale', email: 'owner@royalfortune.example', side: 'client' },
      { name: 'Mihai Pop', email: 'mihai@prisma.example', side: 'team' },
    ],
    noteTaker: 'none',
    summary: [],
    actionItems: [],
    transcript: [],
  },
];

export const callById = (id: string) => MOCK_CALLS.find((c) => c.id === id);
